import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useCreateTicket } from "@/hooks/useSupportTickets";
import { useCompanySelector } from "@/hooks/useCompanySelector";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ReportIssueDialog = ({ open, onOpenChange }: Props) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const { activeCompanyId } = useCompanySelector();
  const createTicket = useCreateTicket();

  const handleSubmit = async () => {
    if (!subject.trim() || !activeCompanyId) return;
    try {
      const ticket = await createTicket.mutateAsync({
        subject: subject.trim(),
        body: body.trim(),
        priority,
        companyId: activeCompanyId,
      });
      const ref = ticket?.ticket_number ? `TKT-${String(ticket.ticket_number).padStart(5, "0")}` : "";
      toast.success(`Ticket ${ref} submitted. Our team will respond soon.`);
      setSubject("");
      setBody("");
      setPriority("normal");
      onOpenChange(false);
    } catch {
      toast.error("Failed to submit ticket. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Report an Issue</DialogTitle>
          <DialogDescription>
            Describe your issue and our support team will get back to you.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ticket-subject">Subject</Label>
            <Input
              id="ticket-subject"
              placeholder="Brief description of the issue"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ticket-body">Details</Label>
            <Textarea
              id="ticket-body"
              placeholder="Provide more details about the issue…"
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!subject.trim() || createTicket.isPending}
          >
            {createTicket.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Submit Ticket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
